<template>
  <section class="page">
    <h2 class="page__title">{{ $t('tsog.title') }}</h2>

    <p v-if="loading">{{ $t('common.loading') }}</p>
    <p v-else-if="error" class="error">{{ $t('common.error') }}: {{ error }}</p>

    <div v-else class="table-container">
      <div class="table-wrapper">
      <table class="tsog-table">
        <thead>
          <tr>
            <th>{{ $t('tsog.columns.kind') }}</th>
            <th>{{ $t('tsog.columns.meterReadings') }}</th>
            <th>{{ $t('tsog.columns.sectorSensor') }}</th>
            <th>{{ $t('tsog.columns.signalType') }}</th>
            <th>{{ $t('tsog.columns.signalReceivedAt') }}</th>
            <th>{{ $t('tsog.columns.dueTo') }}</th>
            <th>{{ $t('tsog.columns.whoInformed') }}</th>
            <th>{{ $t('tsog.columns.alarmGroupArrival') }}</th>
            <th>{{ $t('tsog.columns.coverGroupArrival') }}</th>
            <th>{{ $t('tsog.columns.triggerReason') }}</th>
            <th>{{ $t('tsog.columns.reasonSetBy') }}</th>
            <th>{{ $t('tsog.columns.signature') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, idx) in rows"
            :key="row.kind || idx"
            :class="['tsog-row', { even: idx % 2 === 0 }]"
          >
            <td class="kind" :data-kind="row.kind">{{ kindLabel(row.kind) }}</td>
            <td><input v-model="row.meter_readings" type="text" :disabled="!canEdit" /></td>
            <td><input v-model="row.sector_sensor_number" type="text" :disabled="!canEdit" /></td>
            <td><input v-model="row.signal_type" type="text" :disabled="!canEdit" /></td>
            <td><input v-model="row.signal_received_at" type="text" :disabled="!canEdit" /></td>
            <td><input v-model="row.due_to" type="text" :disabled="!canEdit" /></td>
            <td><input v-model="row.who_informed" type="text" :disabled="!canEdit" /></td>
            <td><input v-model="row.alarm_group_arrival_time" type="text" :disabled="!canEdit" /></td>
            <td><input v-model="row.cover_group_arrival_time" type="text" :disabled="!canEdit" /></td>
            <td><input v-model="row.trigger_reason" type="text" :disabled="!canEdit" /></td>
            <td><input v-model="row.reason_set_by" type="text" :disabled="!canEdit" /></td>
            <td><input v-model="row.signature" type="text" :disabled="!canEdit" /></td>
          </tr>
        </tbody>
      </table>
      </div>

      <div class="actions">
        <button v-if="canEdit" class="btn" :disabled="saving" @click="save">{{ $t('common.save') }}</button>
      </div>
    </div>
  </section>
</template>

<script>
import { checkOrCreateTsogByDate, updateTsogSheet } from '@/api/tsog'
import { ensureCurrentUser, getCachedCurrentUser } from '@/api/user'
import { canEditTsog } from '@/access/armAccess'

function todayISO() {
  const d = new Date()
  const two = (n) => (n < 10 ? `0${n}` : String(n))
  return `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}`
}

function defaultRows() {
  const kinds = [
    'По штату',
    'По списку',
    'В расходе',
    'Прибыло на усиление',
    'Всего в подразделении',
    'Использовалось в охране границы',
  ]
  return kinds.map((kind) => ({
    kind,
    meter_readings: '',
    sector_sensor_number: '',
    signal_type: '',
    signal_received_at: '',
    due_to: '',
    who_informed: '',
    alarm_group_arrival_time: '',
    cover_group_arrival_time: '',
    trigger_reason: '',
    reason_set_by: '',
    signature: '',
  }))
}

export default {
  name: 'TsogView',
  data() {
    return {
      currentUser: null,
      loading: false,
      saving: false,
      error: null,
      sheet: null,
      rows: defaultRows(),
    }
  },
  computed: {
    armUser() {
      return this.currentUser || getCachedCurrentUser();
    },
    canEdit() {
      return canEditTsog(this.armUser);
    },
  },
  mounted() {
    this.loadCurrentUser()
    this.load()
  },
  methods: {
    async loadCurrentUser() {
      try {
        this.currentUser = await ensureCurrentUser();
      } catch {
        this.currentUser = getCachedCurrentUser();
      }
    },
    kindLabel(kind) {
      if (!kind) return ''
      const key = `tsog.kinds.${kind}`
      const translated = this.$t(key)
      return translated === key ? kind : translated
    },
    async load() {
      this.loading = true
      this.error = null
      try {
        const sheet = await checkOrCreateTsogByDate(todayISO())
        this.sheet = sheet
        const incoming = Array.isArray(sheet?.rows) ? sheet.rows : []
        this.rows = incoming.length ? incoming : defaultRows()
      } catch (e) {
        this.error = e?.payload?.detail || e?.message || this.$t('tsog.loadError')
      } finally {
        this.loading = false
      }
    },
    async save() {
      if (!this.canEdit) return
      if (!this.sheet?.id) return
      this.saving = true
      this.error = null
      try {
        const payload = {
          id: this.sheet.id,
          date: this.sheet.date,
          rows: this.rows,
        }
        const updated = await updateTsogSheet(this.sheet.id, payload)
        this.sheet = updated
      } catch (e) {
        this.error = e?.payload?.detail || e?.message || this.$t('tsog.saveError')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.page {
  padding: 2rem;
  background: #f5f7fa;
  min-height: 100vh;
}

.page__title {
  font-size: 2rem;
  font-weight: bold;
  margin-bottom: 1.5rem;
  color: #000;
}

.error {
  color: #D81717;
}

/* Table styling aligned with BSFP/attendance */
.table-container {
  background: white;
  border-radius: 1rem;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.table-wrapper {
  overflow-x: auto;
}

.tsog-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  min-width: 1600px;
}

.tsog-table thead {
  background: #f8fafc;
  border-bottom: 2px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 10;
}

.tsog-table th {
  padding: 0.75rem 0.5rem;
  text-align: center;
  font-weight: 600;
  font-size: 0.75rem;
  color: #64748b;
  border-right: 1px solid #e2e8f0;
  text-transform: uppercase;
}

.tsog-table th:last-child {
  border-right: none;
}

.tsog-table td {
  padding: 0;
  border-right: 1px solid #e2e8f0;
  vertical-align: top;
}

.tsog-table td:last-child {
  border-right: none;
}

.tsog-table tbody tr {
  border-bottom: 1px solid #f1f5f9;
  transition: background-color 0.15s ease;
}

.tsog-table tbody tr.even {
  background: white;
}

.tsog-table tbody tr:not(.even) {
  background: #f9fafb;
}

.tsog-table tbody tr:hover {
  background: #eff6ff !important;
}

.kind {
  width: 200px;
  padding: 0.75rem 0.5rem !important;
  font-size: 0.85rem;
  color: #1e293b;
  text-align: left;
  font-weight: 600;
  background: #f8fafc;
  border-right: 2px solid #e2e8f0 !important;
  white-space: normal;
  word-break: break-word;
}

.tsog-table input {
  width: 100%;
  display: block;
  border: 1px solid transparent;
  border-radius: 0.5rem;
  padding: 0.75rem 0.5rem;
  background: transparent;
  outline: none;
  font-size: 0.85rem;
  color: #1e293b;
}

.tsog-table input:focus {
  background: white;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.actions {
  padding: 0.75rem 1rem;
  border-top: 2px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
}

.btn {
  padding: 0.5rem 1rem;
  background: white;
  border: 1px solid #cbd5e1;
  border-radius: 0.5rem;
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 600;
  transition: all 0.2s;
  color: #475569;
  white-space: nowrap;
}

.btn:hover {
  background: #f8fafc;
  border-color: #94a3b8;
}

.btn:disabled {
  opacity: 0.6;
  cursor: default;
}
</style>
